<template>
  <div class="page-header">
    <!-- Background Layer -->
    <div class="background-layer">
      <div class="content-bg"></div>
      <div class="header-bg">
        <img src="@/assets/layout-header-bg.png" alt="" class="header-wave-img" />
      </div>
    </div>

    <!-- Header Content -->
    <div class="header-content">
      <div class="top-bar">
        <div class="title-section">
          <button 
            v-if="showBackButton" 
            @click="handleBack" 
            class="back-button"
          >
            <i class="mdi mdi-arrow-left"></i>
          </button>
          <h1 class="page-title">{{ title }}</h1>
        </div>
        <div class="bert-points-logo">
          <img src="@/assets/berts-logo-header.png" alt="Bert Points" class="logo" />
        </div>
      </div>
    </div>

    <!-- Notification Card (optional) -->
    <div v-if="showNotification" class="notification-card-wrapper">
      <div class="notification-card">
        <span class="notification-text">{{ notificationText }}</span>
        <button 
          v-if="notificationButtonText" 
          class="notification-btn" 
          @click="handleNotificationClick"
        >
          {{ notificationButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AppPageHeader",
  props: {
    title: {
      type: String,
      required: true
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    showNotification: {
      type: Boolean,
      default: false
    },
    notificationText: {
      type: String,
      default: ""
    },
    notificationButtonText: {
      type: String,
      default: ""
    }
  },
  methods: {
    handleBack() {
      this.$emit('back');
      // Default behavior: go back in history
      if (!this.$listeners.back) {
        this.$router.go(-1);
      }
    },
    
    handleNotificationClick() {
      this.$emit('notification-click');
    }
  }
};
</script>

<style scoped>
/* ========================================= */
/* BACKGROUND LAYER (Desktop Default)        */
/* ========================================= */
.page-header {
  position: relative;
  width: 100%;
  z-index: 10;
}

.background-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.content-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #f8f9fa;
  z-index: 1;
}

.header-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 320px; /* Desktop Height */
  z-index: 2;
  transition: height 0.3s ease;
}

.header-wave-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: bottom;
}

/* ========================================= */
/* HEADER CONTENT                             */
/* ========================================= */
.header-content {
  position: relative;
  z-index: 10;
  padding: 32px 0px 24px 0px;
  max-width: 1200px;
  margin: 0 32px;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.back-button {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
}

.back-button i {
  font-size: 1.2rem;
}

.page-title {
  color: white;
  font-weight: 700;
  margin: 0;
  font-size: 2rem;
}

.bert-points-logo .logo {
  height: 40px;
  background: white;
  padding: 4px;
  border-radius: 8px;
}

/* ========================================= */
/* NOTIFICATION CARD                          */
/* ========================================= */
.notification-card-wrapper {
  position: relative;
  z-index: 10;
  padding: 0 0px 32px 0px;
  max-width: 1200px;
  margin: 0 32px;
}

.notification-card {
  background: white;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.notification-text {
  font-weight: 600;
  color: #333;
}

.notification-btn {
  background: #F2994A;
  color: white;
  border: none;
  padding: 0.6rem 1.5rem;
  border-radius: 20px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;
}

.notification-btn:hover {
  background: #e0874a;
  transform: translateY(-1px);
}

/* ========================================= */
/* MOBILE RESPONSIVE STYLES                  */
/* ========================================= */
@media (max-width: 1024px) {
  .notification-card-wrapper {
    padding: 24px 32px;
    margin: 0;
  }
  .header-content {
    padding: 24px 32px;
    margin: 0;
  }
}

@media (max-width: 768px) {
  .header-bg {
    height: 300px;
  }

  .header-content {
    padding: 24px 20px 16px 20px;
    margin: 0;
  }

  .page-title {
    font-size: 1.75rem;
  }

  .bert-points-logo .logo {
    height: 32px;
  }

  .back-button {
    width: 40px;
    height: 40px;
  }

  .back-button i {
    font-size: 1.1rem;
  }

  .notification-card-wrapper {
    padding: 0 20px 24px 20px;
    margin: 0;
  }

  .notification-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 16px;
  }

  .notification-text {
    font-size: 0.95rem;
    width: 100%;
  }

  .notification-btn {
    width: 100%;
    text-align: center;
  }
}
</style>